import type { JSX, ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { Badge, Button, Dialog, Select, Textarea, useToast, type BadgeTone } from '@artosera/ui';
import {
  CHOIX_MODULE_DEMANDE,
  CHOIX_RETENUS,
  MOTIF_PERTE_MAX,
  type ChoixAccompagnement,
  type ChoixModuleDemande,
  type DemandeOuvertureVue,
} from '@artosera/shared';
import { usePeut } from '../../auth/usePeut.js';
import { useSession } from '../../auth/useSession.js';
import {
  creerDossierDemandeOuverture,
  modifierDemandeOuverture,
  obtenirDemandeOuverture,
  perdreDemandeOuverture,
  qualifierDemandeOuverture,
} from '../../lib/api.js';
import {
  CLE_DEMANDES,
  TONE_ETAT_DEMANDE,
  ancienneteEnJours,
  cleDemande,
  erreurDemande,
  formaterDateHeure,
  libelleAnciennete,
  lienSiteWeb,
  nomDuPays,
  type ErreurDemande,
} from '../../lib/demandesOuverture.js';
import { useLibellesModules } from '../../lib/modules.js';
import pageStyles from '../../styles/page.module.css';
import styles from './DemandesOuverture.module.css';

const PERSONNE = 'personne';
const TONE_ACCOMPAGNEMENT: Record<ChoixAccompagnement, BadgeTone> = {
  oui: 'available',
  'a-discuter': 'reserved',
  non: 'withdrawn',
};

/** `/demandes/:id` : la fiche d'une demande d'ouverture (ARTO-188). Ce chemin est celui du courriel d'alerte. */
export function DemandeOuvertureDetail(): JSX.Element {
  const { t, i18n } = useTranslation('console');
  const peut = usePeut();
  const { moi } = useSession();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryClient = useQueryClient();
  const libelles = useLibellesModules();
  const id = useParams<{ id: string }>().id ?? '';

  const [erreur, setErreur] = useState<ErreurDemande | null>(null);
  const [geste, setGeste] = useState<'creer' | 'autre' | null>(null);
  const occupe = geste !== null;
  // Deux clics dans la même image ne passent pas par `occupe` : ce drapeau les écarte.
  const enCours = useRef(false);
  const [perdre, setPerdre] = useState(false);
  const [motif, setMotif] = useState('');
  const [motifErreur, setMotifErreur] = useState(false);
  const [note, setNote] = useState('');

  const requete = useQuery({
    queryKey: cleDemande(id),
    queryFn: () => obtenirDemandeOuverture(id),
    enabled: id !== '',
  });

  if (requete.isLoading) return <p className={pageStyles.videEtat}>{t('commun.chargement')}</p>;
  if (requete.isError || !requete.data) {
    return (
      <section>
        <p className={pageStyles.videEtat}>{t('demandesOuverture.introuvable')}</p>
        <Link to="/demandes" className="lien-cible">
          {t('demandesOuverture.retour')}
        </Link>
      </section>
    );
  }

  const demande = requete.data;
  const peutGerer = peut('console:manage-onboarding');
  // `gestes.perdre` vaut « la demande se travaille encore » : le responsable ne se choisit que là.
  const ouverte = demande.gestes.perdre;
  const date = (iso: string): string => formaterDateHeure(iso, i18n.language);

  /**
   * Un geste d'écriture : un seul à la fois, à la révision de la dernière vue
   * reçue. Rend `true` s'il a abouti ; sinon l'erreur s'affiche en tête de page.
   */
  const lancer = async <R,>(
    nom: 'creer' | 'autre',
    appel: (courante: DemandeOuvertureVue) => Promise<R>,
    apres: (reponse: R) => void,
  ): Promise<boolean> => {
    if (enCours.current) return false;
    enCours.current = true;
    setGeste(nom);
    setErreur(null);
    try {
      apres(await appel(demande));
      void queryClient.invalidateQueries({ queryKey: CLE_DEMANDES });
      return true;
    } catch (echec) {
      setErreur(erreurDemande(t, echec));
      return false;
    } finally {
      enCours.current = false;
      setGeste(null);
    }
  };

  const poser = (vue: DemandeOuvertureVue): void => {
    queryClient.setQueryData(cleDemande(id), vue);
  };
  const confirmer = (cle: string): void =>
    toast({ title: t(cle), closeLabel: t('saisies.suppression.fermer') });

  const qualifier = (): Promise<boolean> =>
    lancer(
      'autre',
      (courante) => qualifierDemandeOuverture(courante.id, courante.revision),
      (vue) => {
        poser(vue);
        confirmer('demandesOuverture.fiche.qualifiee');
      },
    );

  const creerDossier = (): Promise<boolean> =>
    lancer(
      'creer',
      (courante) => creerDossierDemandeOuverture(courante.id, courante.revision),
      (reponse) => {
        poser(reponse.demande);
        void navigate({ pathname: `/ouvertures/${reponse.dossier.id}/assistant`, search });
      },
    );

  const confirmerPerte = async (): Promise<void> => {
    const texte = motif.trim();
    if (texte.length < 3) {
      setMotifErreur(true);
      return;
    }
    const fait = await lancer(
      'autre',
      (courante) => perdreDemandeOuverture(courante.id, courante.revision, texte),
      (vue) => {
        poser(vue);
        confirmer('demandesOuverture.fiche.perdue.succes');
      },
    );
    // La fenêtre se ferme aussi sur un échec, pour montrer l'erreur ; le motif reste saisi.
    setPerdre(false);
    if (fait) setMotif('');
  };

  const choisirResponsable = (valeur: string): Promise<boolean> =>
    lancer(
      'autre',
      (courante) =>
        modifierDemandeOuverture(courante.id, {
          revision: courante.revision,
          responsableId: valeur === PERSONNE ? null : valeur,
        }),
      (vue) => {
        poser(vue);
        confirmer('demandesOuverture.fiche.responsableMaj');
      },
    );

  const ajouterNote = async (): Promise<void> => {
    const texte = note.trim();
    if (texte === '') return;
    const fait = await lancer(
      'autre',
      (courante) =>
        modifierDemandeOuverture(courante.id, { revision: courante.revision, note: texte }),
      (vue) => {
        poser(vue);
        confirmer('demandesOuverture.fiche.notePosee');
      },
    );
    if (fait) setNote('');
  };

  const recharger = async (): Promise<void> => {
    setErreur(null);
    await requete.refetch();
  };

  const nom = demande.galerie.nom === '' ? t('demandesOuverture.sansNom') : demande.galerie.nom;
  const jours = ancienneteEnJours(demande.creeLe);
  const siteWeb = demande.galerie.siteWeb === null ? null : lienSiteWeb(demande.galerie.siteWeb);
  const aucunGeste =
    !demande.gestes.qualifier && !demande.gestes.creerDossier && !demande.gestes.perdre;

  // Le responsable courant, « moi », « personne » : celui qui n'est ni l'un ni l'autre reste lisible.
  const moiId = moi?.compte.id ?? null;
  const responsableId = demande.responsable?.id ?? PERSONNE;
  const optionsResponsable = [
    { value: PERSONNE, label: t('demandesOuverture.filtres.personne') },
    ...(moiId === null
      ? []
      : [
          {
            value: moiId,
            label: `${t('demandesOuverture.filtres.moi')} (${moi?.compte.email ?? ''})`,
          },
        ]),
    ...(demande.responsable !== null && demande.responsable.id !== moiId
      ? [{ value: demande.responsable.id, label: demande.responsable.email }]
      : []),
  ];

  return (
    <section>
      <p>
        <Link to="/demandes" className="lien-cible">
          {t('demandesOuverture.retour')}
        </Link>
      </p>

      <div className={pageStyles.entete}>
        <div className={styles.enteteFiche}>
          <h1>{nom}</h1>
          <div className={styles.meta}>
            <span className={styles.mono}>{demande.numero}</span>
            <Badge tone={TONE_ETAT_DEMANDE[demande.etat]}>
              {t(`demandesOuverture.etat.${demande.etat}`)}
            </Badge>
            <span>{t(`demandesOuverture.source.${demande.source}`)}</span>
            <span>
              {t('demandesOuverture.fiche.recue', { date: date(demande.creeLe) })} (
              {libelleAnciennete(t, jours).toLowerCase()})
            </span>
          </div>
        </div>
      </div>

      {erreur ? (
        <div role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
          <span>{erreur.message}</span>
          <span className={styles.rechargement}>
            {erreur.dossierId ? (
              <Link
                to={{ pathname: `/ouvertures/${erreur.dossierId}`, search }}
                className={`lien-cible ${styles.lienFiche}`}
              >
                {t('demandesOuverture.erreurs.demande_convertie_lien')}
              </Link>
            ) : null}
            {erreur.recharger ? (
              <Button variant="secondary" size="sm" onClick={() => void recharger()}>
                {t('demandesOuverture.recharger')}
              </Button>
            ) : null}
          </span>
        </div>
      ) : null}

      {demande.dossier ? (
        <div className={pageStyles.banniere} role="status">
          <span>{t('demandesOuverture.fiche.dossier.titre')}</span>
          <Link
            to={{ pathname: `/ouvertures/${demande.dossier.id}`, search }}
            className={`lien-cible ${styles.lienFiche}`}
          >
            {t('demandesOuverture.fiche.dossier.lien', { numero: demande.dossier.numero })}
          </Link>
        </div>
      ) : null}
      {demande.etat === 'perdue' && demande.motifPerte ? (
        <div className={pageStyles.banniere} role="status">
          <span>
            <strong>{t('demandesOuverture.fiche.motifPerte')}</strong>
            {' : '}
            <span className={styles.motif}>{demande.motifPerte}</span>
          </span>
        </div>
      ) : null}

      <div className={styles.gestes}>
        {!peutGerer ? (
          <p>{t('demandesOuverture.fiche.gestes.lectureSeule')}</p>
        ) : aucunGeste ? (
          <p>{t('demandesOuverture.fiche.gestes.aucun')}</p>
        ) : (
          <>
            {demande.gestes.creerDossier ? (
              <Button disabled={occupe} onClick={() => void creerDossier()}>
                {geste === 'creer'
                  ? t('demandesOuverture.fiche.gestes.creerDossierEnCours')
                  : t('demandesOuverture.fiche.gestes.creerDossier')}
              </Button>
            ) : null}
            {demande.gestes.qualifier ? (
              <Button variant="secondary" disabled={occupe} onClick={() => void qualifier()}>
                {t('demandesOuverture.fiche.gestes.qualifier')}
              </Button>
            ) : null}
            {demande.gestes.perdre ? (
              <Button variant="danger" disabled={occupe} onClick={() => setPerdre(true)}>
                {t('demandesOuverture.fiche.gestes.perdre')}
              </Button>
            ) : null}
          </>
        )}
      </div>

      <div className={styles.colonnes}>
        <div className={styles.pile}>
          <section className={pageStyles.panneau} aria-labelledby="demande-selection">
            <div className={pageStyles.sectionTitre}>
              <h2 id="demande-selection">{t('demandesOuverture.fiche.sections.selection')}</h2>
            </div>
            <Selection demande={demande} libelles={libelles} />
          </section>

          <section className={pageStyles.panneau} aria-labelledby="demande-message">
            <div className={pageStyles.sectionTitre}>
              <h2 id="demande-message">{t('demandesOuverture.fiche.sections.message')}</h2>
            </div>
            {demande.message === '' ? (
              <p className={styles.muet}>{t('demandesOuverture.fiche.messageVide')}</p>
            ) : (
              <p className={styles.message}>{demande.message}</p>
            )}
          </section>

          <section className={pageStyles.panneau} aria-labelledby="demande-notes">
            <div className={pageStyles.sectionTitre}>
              <h2 id="demande-notes">{t('demandesOuverture.fiche.sections.notes')}</h2>
            </div>
            {demande.notes.length === 0 ? (
              <p className={styles.vide}>{t('demandesOuverture.fiche.notesVide')}</p>
            ) : (
              <ul className={styles.notes}>
                {demande.notes.map((ligne, rang) => (
                  <li key={rang}>
                    {ligne.texte}
                    <span className={styles.noteMeta}>
                      {t('demandesOuverture.fiche.noteParLe', {
                        par: ligne.par,
                        date: date(ligne.le),
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {peutGerer ? (
              <div className={styles.champ}>
                <label htmlFor="demande-note">{t('demandesOuverture.fiche.noteChamp')}</label>
                <Textarea
                  id="demande-note"
                  rows={3}
                  maxLength={2000}
                  value={note}
                  onChange={(evenement) => setNote(evenement.target.value)}
                />
                <div>
                  <Button
                    variant="secondary"
                    disabled={occupe || note.trim() === ''}
                    onClick={() => void ajouterNote()}
                  >
                    {t('demandesOuverture.fiche.noteAjouter')}
                  </Button>
                </div>
              </div>
            ) : null}
          </section>

          <section className={pageStyles.panneau} aria-labelledby="demande-historique">
            <div className={pageStyles.sectionTitre}>
              <h2 id="demande-historique">{t('demandesOuverture.fiche.sections.historique')}</h2>
            </div>
            <ol className={styles.historique}>
              {demande.historique.map((ligne, rang) => (
                <li key={rang}>
                  <div className={styles.historiqueTete}>
                    <Badge tone={TONE_ETAT_DEMANDE[ligne.etat]}>
                      {t(`demandesOuverture.etat.${ligne.etat}`)}
                    </Badge>
                    <span className={styles.muet}>
                      {t('demandesOuverture.fiche.historiqueLigne', {
                        par: ligne.par,
                        date: date(ligne.le),
                      })}
                    </span>
                  </div>
                  {ligne.motif ? <p className={styles.motif}>{ligne.motif}</p> : null}
                </li>
              ))}
            </ol>
          </section>
        </div>

        <div className={styles.pile}>
          <section className={pageStyles.panneau} aria-labelledby="demande-responsable">
            <div className={pageStyles.sectionTitre}>
              <h2 id="demande-responsable">{t('demandesOuverture.fiche.responsable')}</h2>
            </div>
            {peutGerer && ouverte ? (
              <div className={styles.champ}>
                <label htmlFor="demande-responsable-choix">
                  {t('demandesOuverture.fiche.responsableChamp')}
                </label>
                <Select
                  id="demande-responsable-choix"
                  aria-describedby="demande-responsable-aide"
                  value={responsableId}
                  disabled={occupe}
                  onValueChange={(valeur) => {
                    if (valeur !== responsableId) void choisirResponsable(valeur);
                  }}
                  options={optionsResponsable}
                />
                <span id="demande-responsable-aide" className={styles.aide}>
                  {t('demandesOuverture.fiche.responsableAide')}
                </span>
              </div>
            ) : (
              <p>
                {demande.responsable?.email ?? (
                  <span className={styles.muet}>{t('demandesOuverture.sansResponsable')}</span>
                )}
              </p>
            )}
          </section>

          <section className={pageStyles.panneau} aria-labelledby="demande-contact">
            <div className={pageStyles.sectionTitre}>
              <h2 id="demande-contact">{t('demandesOuverture.fiche.sections.contact')}</h2>
            </div>
            <dl className={styles.faits}>
              <Fait cle={t('demandesOuverture.fiche.contact.nom')}>{demande.contact.nom}</Fait>
              <Fait cle={t('demandesOuverture.fiche.contact.email')}>
                <a href={`mailto:${demande.contact.email}`}>{demande.contact.email}</a>
              </Fait>
              <Fait cle={t('demandesOuverture.fiche.contact.telephone')}>
                {demande.contact.telephone ?? <Absent />}
              </Fait>
              <Fait cle={t('demandesOuverture.fiche.contact.fonction')}>
                {demande.contact.fonction ?? <Absent />}
              </Fait>
              <Fait cle={t('demandesOuverture.fiche.contact.langue')}>
                {t(`demandesOuverture.fiche.langue.${demande.langue}`)}
              </Fait>
            </dl>
          </section>

          <section className={pageStyles.panneau} aria-labelledby="demande-galerie">
            <div className={pageStyles.sectionTitre}>
              <h2 id="demande-galerie">{t('demandesOuverture.fiche.sections.galerie')}</h2>
            </div>
            <dl className={styles.faits}>
              <Fait cle={t('demandesOuverture.fiche.galerie.nom')}>
                {demande.galerie.nom === '' ? <Absent /> : demande.galerie.nom}
              </Fait>
              <Fait cle={t('demandesOuverture.fiche.galerie.ville')}>
                {demande.galerie.ville ?? <Absent />}
              </Fait>
              <Fait cle={t('demandesOuverture.fiche.galerie.pays')}>
                {demande.galerie.pays === null ? (
                  <Absent />
                ) : (
                  nomDuPays(demande.galerie.pays, i18n.language)
                )}
              </Fait>
              <Fait cle={t('demandesOuverture.fiche.galerie.siteWeb')}>
                {demande.galerie.siteWeb === null ? (
                  <Absent />
                ) : siteWeb === null ? (
                  demande.galerie.siteWeb
                ) : (
                  <a href={siteWeb} target="_blank" rel="noopener noreferrer">
                    {demande.galerie.siteWeb}
                  </a>
                )}
              </Fait>
            </dl>
          </section>

          <section className={pageStyles.panneau} aria-labelledby="demande-envois">
            <div className={pageStyles.sectionTitre}>
              <h2 id="demande-envois">{t('demandesOuverture.fiche.sections.envois')}</h2>
            </div>
            <dl className={styles.faits}>
              <Fait cle={t('demandesOuverture.fiche.envois.accuse')}>
                <Envoi iso={demande.envois.accuse} date={date} />
              </Fait>
              <Fait cle={t('demandesOuverture.fiche.envois.alerte')}>
                <Envoi iso={demande.envois.alerte} date={date} />
              </Fait>
            </dl>
          </section>

          <section className={pageStyles.panneau} aria-labelledby="demande-consentement">
            <div className={pageStyles.sectionTitre}>
              <h2 id="demande-consentement">
                {t('demandesOuverture.fiche.sections.consentement')}
              </h2>
            </div>
            <p>
              {t('demandesOuverture.fiche.consentement', {
                version: demande.consentement.version,
                date: date(demande.consentement.le),
              })}
            </p>
          </section>
        </div>
      </div>

      <Dialog
        open={perdre}
        onOpenChange={(ouvert) => {
          setPerdre(ouvert);
          if (!ouvert) setMotifErreur(false);
        }}
        title={t('demandesOuverture.fiche.perdue.titre')}
        description={t('demandesOuverture.fiche.perdue.texte')}
        closeLabel={t('demandesOuverture.fiche.perdue.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPerdre(false)}>
              {t('demandesOuverture.fiche.perdue.annuler')}
            </Button>
            <Button variant="danger" disabled={occupe} onClick={() => void confirmerPerte()}>
              {t('demandesOuverture.fiche.perdue.confirmer')}
            </Button>
          </>
        }
      >
        <div className={styles.champ}>
          <label htmlFor="demande-motif">{t('demandesOuverture.fiche.perdue.motif')}</label>
          <Textarea
            id="demande-motif"
            rows={4}
            maxLength={MOTIF_PERTE_MAX}
            value={motif}
            aria-invalid={motifErreur || undefined}
            aria-describedby={motifErreur ? 'demande-motif-erreur' : undefined}
            onChange={(evenement) => {
              setMotif(evenement.target.value);
              setMotifErreur(false);
            }}
          />
          {motifErreur ? (
            <span id="demande-motif-erreur" role="alert" className={styles.erreurChamp}>
              {t('demandesOuverture.fiche.perdue.motifErreur')}
            </span>
          ) : null}
        </div>
      </Dialog>
    </section>
  );
}

function Fait({ cle, children }: { cle: string; children: ReactNode }): JSX.Element {
  return (
    <>
      <dt>{cle}</dt>
      <dd>{children}</dd>
    </>
  );
}

function Absent(): JSX.Element {
  const { t } = useTranslation('console');
  return <span className={styles.muet}>{t('demandesOuverture.fiche.nonRenseigne')}</span>;
}

/** Un courriel parti (sa date) ou encore dans la file (« en attente »). */
function Envoi({ iso, date }: { iso: string | null; date: (iso: string) => string }): JSX.Element {
  const { t } = useTranslation('console');
  return iso === null ? (
    <Badge tone="reserved">{t('demandesOuverture.fiche.envois.enAttente')}</Badge>
  ) : (
    <>{t('demandesOuverture.fiche.envois.envoyeLe', { date: date(iso) })}</>
  );
}

/** La sélection du prospect, telle que l'API la rend : modules et add-ons groupés par réponse. */
function Selection({
  demande,
  libelles,
}: {
  demande: DemandeOuvertureVue;
  libelles: ReturnType<typeof useLibellesModules>;
}): JSX.Element {
  const { t } = useTranslation('console');
  const { selection } = demande;

  return (
    <>
      <h3 className={styles.sousTitre}>{t('demandesOuverture.fiche.selection.palier')}</h3>
      <p>
        {selection.palier === null ? (
          <span className={styles.muet}>{t('demandesOuverture.sansPalier')}</span>
        ) : (
          t(`galeries.palier.${selection.palier}`)
        )}
      </p>

      <h3 className={styles.sousTitre}>{t('demandesOuverture.fiche.selection.modules')}</h3>
      <GroupesChoix
        lignes={selection.modules.map((ligne) => ({ id: ligne.module, choix: ligne.choix }))}
        libelle={libelles.libelle}
      />

      <h3 className={styles.sousTitre}>{t('demandesOuverture.fiche.selection.addons')}</h3>
      <GroupesChoix
        lignes={selection.addons.map((ligne) => ({ id: ligne.addon, choix: ligne.choix }))}
        libelle={libelles.libelle}
      />

      <h3 className={styles.sousTitre}>{t('demandesOuverture.fiche.selection.services')}</h3>
      {selection.services.length === 0 ? (
        <p className={styles.muet}>{t('demandesOuverture.fiche.selection.aucun')}</p>
      ) : (
        <ul className={styles.pastilles}>
          {selection.services.map((service) => (
            <li key={service} className={styles.pastille}>
              {libelles.libelle(service)}
            </li>
          ))}
        </ul>
      )}

      <h3 className={styles.sousTitre}>{t('demandesOuverture.fiche.selection.accompagnement')}</h3>
      {selection.accompagnement.length === 0 ? (
        <p className={styles.muet}>{t('demandesOuverture.fiche.selection.aucun')}</p>
      ) : (
        <ul className={styles.prestations}>
          {selection.accompagnement.map((ligne) => (
            <li key={ligne.prestation}>
              <span>
                {t(`demandesOuverture.fiche.accompagnement.prestation.${ligne.prestation}`, {
                  defaultValue: ligne.prestation,
                })}
              </span>
              <Badge tone={TONE_ACCOMPAGNEMENT[ligne.choix]}>
                {t(`demandesOuverture.fiche.accompagnement.choix.${ligne.choix}`)}
              </Badge>
            </li>
          ))}
        </ul>
      )}

      {selection.identifiantsIgnores > 0 ? (
        <p className={styles.ignores}>
          {t('demandesOuverture.fiche.selection.ignores', { count: selection.identifiantsIgnores })}
        </p>
      ) : null}
    </>
  );
}

/** Les lignes groupées par réponse, dans l'ordre indispensable, intéressant, plus tard, pas pour nous. */
function GroupesChoix({
  lignes,
  libelle,
}: {
  lignes: readonly { id: string; choix: ChoixModuleDemande }[];
  libelle: (id: string) => string;
}): JSX.Element {
  const { t } = useTranslation('console');
  if (lignes.length === 0) {
    return <p className={styles.muet}>{t('demandesOuverture.fiche.selection.aucun')}</p>;
  }
  return (
    <>
      {CHOIX_MODULE_DEMANDE.map((choix) => {
        const groupe = lignes.filter((ligne) => ligne.choix === choix);
        if (groupe.length === 0) return null;
        const retenu = CHOIX_RETENUS.includes(choix);
        return (
          <div
            key={choix}
            className={`${styles.groupeChoix} ${retenu ? styles.groupeRetenu : ''}`}
            data-choix={choix}
          >
            <h4>
              {t(`demandesOuverture.fiche.choix.${choix}`)}
              <span>{groupe.length}</span>
            </h4>
            <ul className={styles.pastilles}>
              {groupe.map((ligne) => (
                <li key={ligne.id} className={styles.pastille}>
                  {libelle(ligne.id)}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </>
  );
}
